// Helpers for turning a failed axios request into something a user can act on.
//
// Most backend errors come wrapped in a structured body — { detailedMessage, message, errorCode,
// otherErrors, uri } — so a toast should show that, NOT axios's generic "Request failed with
// status code 500". Some endpoints (Spring's own request-binding failures, e.g. a missing/rejected
// query param) instead return RFC-7807 problem+json — { detail, title, status, instance } — so we
// read those too. `apiErrorMessage` pulls the most useful line out of whichever shape came back;
// the raw axios string and the caller's fallback are only last resorts.

// Prefer the backend's specific `detailedMessage` ("GameRegistry not found for id: 123"), then its
// friendlier `message`, then problem+json `detail` / `title`, then axios's generic string, then the
// caller's fallback.
export const apiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
    const data = error?.response?.data;
    return aiLimitMessage(error) || data?.detailedMessage || data?.message || data?.detail || data?.title
        || error?.message || fallback;
};

// The AI usage gate's refusal - out of AI credits (402), too many requests or a spend cap (429/503), the job
// already running (409) - as the user should read it, or null for any other failure. Its body is
// { error: 'AI Limit Reached', code, message, detailedMessage }: `message` is written for the user
// ("This game doesn't have enough AI credits left…"), `detailedMessage` is for logs. A caller that otherwise
// shows its own wording uses this first, so running out of credits never reads as "please try again".
export const aiLimitMessage = (error) => {
    const data = error?.response?.data;
    return data?.error === 'AI Limit Reached' && data?.message ? data.message : null;
};

// True when a failed request means the target simply doesn't exist — a definitive 404 /
// ENTITY_NOT_FOUND — as opposed to a transient failure (network blip / 5xx) worth retrying.
// Lets a caller redirect / show a "not found" state instead of stranding the user.
/** The server refused: no grant on this game / capability (403), e.g. GAME_ACCESS_DENIED. */
export const isForbiddenError = (error) => error?.response?.status === 403;

export const isNotFoundError = (error) =>
    error?.response?.status === 404 || error?.response?.data?.errorCode === 'ENTITY_NOT_FOUND';
